import { css } from '@emotion/react'
import styled from '@emotion/styled'
import {
    ComponentType,
    KeyboardEvent,
    memo,
    useCallback,
    useEffect,
    useRef,
} from 'react'
import { MENU, WHITEBOARD } from '../../../constants/dialogs'
import { DialogState, MODALSTATE } from '../../../state/modals'
import { closeDialog } from '../../../state/modals/state.dialog'
import { AppRouter } from '../../../state/state.router'
import { slugToID } from '../../../state/state.users'
import { closeWhiteboard } from '../../../state/state.whiteboard'
import {
    TransitionTypes,
    motionTransition,
} from '../../../utilities/transitions'
import ButtonDialogClose from '../../Buttons/ButtonDialogClose'
import { useDialog } from '../../Hooks/useModal'
import { useScreenStateSelector } from '../../Hooks/useScreenState'
import { StyledDivider } from '../../common/StyledDivider'
import { getThemeSpacing } from '../../styles/appTheme'
import { palette } from '../../styles/colors'
import shape from '../../styles/shape'
import { PAPER_HEIGHTS } from '../../theme'
import dialogs from './Dialogs'
import Backdrop from './common/Backdrop'

const backdropStyles = {
    opacity: 1,
}
const dialogExpandedStyles = css({
    width: 'calc(100% - 40px)',
    margin: 20,
})

const dialogFullHeightStyles = css({
    height: '100%',
})
const headerStyles = css({
    justifyContent: 'space-between',
    alignItems: 'center',
    display: 'flex',
    width: '100%',
})
const titleStyles = css({
    display: 'flex',
    fontWeight: 500,
    marginLeft: 16,
    justifyContent: 'center',
    color: palette.standardText,
    flex: 1,
})

const DialogRoot = styled('div')({
    position: 'fixed' as const,
    zIndex: PAPER_HEIGHTS.dialog,
    right: 0,
    bottom: 0,
    top: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    margin: 0,
    marginTop: getThemeSpacing(1.5),
    overflowY: 'unset',
    justifyContent: 'center',
})
const DialogContentsContainer = styled('div')({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: 'calc(100% - 64px)',
    maxWidth: 444,
    height: 'auto',
    maxHeight: 'calc(100% - 64px)',
    borderRadius: shape.borderRadius,
    margin: 32,
    overflowY: 'unset',
    background: palette.drawerBackground,
    transition: 'transform 0.3s var(--gentle-spring-ease)',
    transform: 'scale(1)',
    '@starting-style': {
        transform: 'scale(0.95)',
    },
})

const DialogContainer = styled('div')({
    maxHeight: '100%',
    outline: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
})

const handleCloseDialog = (dialogID: string) => {
    const isMenu = dialogID === MENU
    const isWhiteboard = dialogID === WHITEBOARD

    if (isWhiteboard) {
        closeWhiteboard()
    }

    // TODO: look into why dialog.state is potentially nulled out
    if (MODALSTATE.dialog.state?.slug && isMenu) {
        motionTransition(() => closeDialog(true), {
            id: slugToID[MODALSTATE.dialog.state.slug],
            transitionSource: TransitionTypes.MENU,
            transitionDestination: AppRouter.route.lastTransitionSource,
        })
    } else closeDialog(isMenu || isWhiteboard)
}

const Dialog = memo(() => {
    const dialog = useDialog()
    const containerRef = useRef(null)
    const backdropRef = useRef(null)
    const windowHeight = useScreenStateSelector('windowHeight')

    const { state: dialogProps = {}, type: dialogID } = dialog

    const DialogContents: ComponentType<DialogState> = dialogs[dialogID]

    const {
        close = true,
        disableBackdropClick = false,
        expanded,
        fullHeight,
        fullPage,
        title = false,
    } = dialogProps

    const fullDialogStyles = []
    if (expanded) fullDialogStyles.push(dialogExpandedStyles)
    if (fullHeight) fullDialogStyles.push(dialogFullHeightStyles)
    const dialogStyles = fullDialogStyles

    const onCloseDialog = useCallback(() => {
        handleCloseDialog(dialogID)
    }, [dialogID])

    const keydownClose = useCallback(
        (event: KeyboardEvent) => {
            if (!disableBackdropClick && event.key === 'Escape') {
                onCloseDialog()
            }
        },
        [disableBackdropClick, onCloseDialog],
    )

    const containerClick = useCallback(
        (event) => {
            // make sure we're clicking the main container or the backdrop
            if (
                !disableBackdropClick &&
                (event.target === containerRef.current ||
                    event.target === backdropRef.current)
            ) {
                onCloseDialog()
            }
        },
        [disableBackdropClick, onCloseDialog],
    )

    useEffect(() => {
        containerRef.current.focus()
    }, [dialogID])

    return (
        <DialogRoot id="modal" css={dialogStyles} role="presentation">
            <Backdrop
                data-testid="Dialog__Backdrop"
                ref={backdropRef}
                style={backdropStyles}
                aria-hidden
                onClick={containerClick}
            />
            <DialogContainer
                ref={containerRef}
                onClick={containerClick}
                tabIndex={-2}
                onKeyDown={keydownClose}
                data-testid="Container__Dialog"
            >
                <DialogContentsContainer
                    style={{
                        maxHeight: windowHeight - 64,
                        ...(fullPage
                            ? {
                                  height: '90%',
                                  width: '80%',
                                  maxWidth: '90%',
                              }
                            : {}),
                    }}
                >
                    <div
                        css={title ? headerStyles : undefined}
                        data-testid="Fragment__Header"
                    >
                        {!!title && <span css={titleStyles}>{title}</span>}
                        {close && (
                            <ButtonDialogClose
                                title={!!title}
                                onClick={onCloseDialog}
                            />
                        )}
                    </div>
                    {!!title && (
                        <StyledDivider
                            css={{
                                width: 'calc(100% - 16px)',
                                margin: 'auto',
                                height: 1,
                            }}
                        />
                    )}
                    <DialogContents
                        {...dialogProps}
                        {...(!close ? { onClose: onCloseDialog } : {})}
                    />
                </DialogContentsContainer>
            </DialogContainer>
        </DialogRoot>
    )
})

export default Dialog
